import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useNotification } from '@refinedev/core';
import { ThreeDots } from 'react-loader-spinner';
import TodaysSchedule from './todays-schedule';
import StatCard from './stat-card';
import { semanticColors } from '../../theme';
import useApiService from '../../hooks/useApiService';
import { FacilityDashboardResponse } from './types';
import { FacilityContext } from '../../components/layout';
import {
  CheckBadgeIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  ArrowPathIcon,
  ClockIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

export default function Dashboard() {
  const api = useApiService();
  const { open } = useNotification();
  const { facility } = useContext(FacilityContext);
  const [dashboardData, setDashboardData] = useState<FacilityDashboardResponse | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState<boolean>(true);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  const fetchDashboardData = useCallback(async () => {
    if (!facility?.facility_id) {
      setDashboardError('Facility ID not available');
      setDashboardLoading(false);
      return;
    }

    try {
      setDashboardLoading(true);
      setDashboardError(null);
      
      // Get timezone dynamically
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Use the facility dashboard endpoint with dynamic facility ID and timezone
      const response = await api.get<FacilityDashboardResponse>(
        `/dashboard/facility-dashboard?facilityId=${facility.facility_id}&timeZone=${encodeURIComponent(timeZone)}`
      );
      
      // Set the data directly from API response
      setDashboardData(response.data);
      
    } catch (error) {
      let errorMessage = 'Failed to load dashboard data';
      if (error instanceof Error) errorMessage = error.message;
      else if (typeof error === 'string') errorMessage = error;

      setDashboardError(errorMessage);
      open?.({
        type: 'error',
        message: 'Dashboard Error',
        description: errorMessage,
      });
    } finally {
      setDashboardLoading(false);
    }
  }, [api, open, facility?.facility_id]);

  useEffect(() => {
    if (facility?.facility_id) {
      fetchDashboardData();
    }
  }, [fetchDashboardData, facility?.facility_id]);

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const stats = dashboardData?.stats;
  const todaysSchedule = dashboardData?.todaysSchedule || [];

  const scheduleData = todaysSchedule;

  return (
    <div className="min-h-screen" style={{ backgroundColor: semanticColors.background.primary }}>
      {/* Header - Compact Desktop */}
      <div className="relative overflow-hidden rounded-xl" style={{ background: semanticColors.background.gradient }}>
        <div className="absolute inset-0 bg-black/10 rounded-xl"></div>
        <div className="relative p-4 sm:p-6 lg:px-6 lg:py-4">
          <div className="mx-auto max-w-6xl">
            <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center sm:gap-4">
              <div>
                <h1 
                  className="text-2xl font-bold sm:text-3xl lg:text-2xl"
                  style={{
                    background: 'linear-gradient(to right, rgb(245, 185, 87), rgb(255, 237, 205), rgb(171, 109, 10))',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    fontFamily: 'Inter, sans-serif'
                  }}
                >
                  Dashboard Overview
                </h1>
                <p className="mt-1 text-sm text-white/90 sm:text-base lg:text-sm">
                  Real-time statistics and insights for your facility shifts
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Compact Desktop */}
      <main className="mx-auto max-w-6xl p-4 sm:p-6 lg:px-6 lg:py-4">
        <div className="space-y-4 sm:space-y-5 lg:space-y-4">
          {/* Loading State - Updated with Logo and ThreeDots */}
          {dashboardLoading && (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center space-y-4">
                <img src="/logo-dark-mono.png" alt="VitalHive Logo" style={{ height: "150px" }} />
                <ThreeDots
                  visible={true}
                  height="60"
                  width="60"
                  color="#fbb03b"
                  radius="9"
                  ariaLabel="three-dots-loading"
                  wrapperStyle={{}}
                  wrapperClass=""
                />
                <p className="text-sm font-medium text-gray-700">Loading dashboard...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {dashboardError && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-lg">
              <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-red-100">
                <ExclamationTriangleIcon className="size-8 text-red-600" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-red-800">Unable to Load Dashboard</h3>
              <p className="mb-6 text-red-600">{dashboardError}</p>
              <button
                onClick={handleRefresh}
                className="inline-flex items-center rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:shadow-xl"
                style={{ backgroundColor: semanticColors.button.danger.background }}
              >
                <ArrowPathIcon className="mr-2 size-4" />
                Try Again
              </button>
            </div>
          )}

          {/* Dashboard Content */}
          {!dashboardLoading && !dashboardError && dashboardData && (
            <>
              {/* Stats Cards - Compact Desktop */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6 sm:gap-4 lg:gap-3">
                <StatCard
                  title="OPEN SHIFTS"
                  value={stats?.open || 0}
                  Icon={ClockIcon}
                  color="#D4A123"
                  iconColor="#D4A123"
                  borderColor="#D4A123"
                />
                <StatCard
                  title="CONFIRMED SHIFTS"
                  value={stats?.confirmed || 0}
                  Icon={CheckBadgeIcon}
                  color="#3A7D44"
                  iconColor="#3A7D44"
                  borderColor="#3A7D44"
                />
                <StatCard
                  title="SELECTED SHIFTS"
                  value={stats?.selected || 0}
                  Icon={ClipboardDocumentCheckIcon}
                  color="#D4A123"
                  iconColor="#D4A123"
                  borderColor="#D4A123"
                />
                <StatCard
                  title="COMPLETED SHIFTS"
                  value={stats?.completed || 0}
                  Icon={UserIcon}
                  color="#4A6FA5"
                  iconColor="#4A6FA5"
                  borderColor="#4A6FA5"
                />
                <StatCard
                  title="CANCELLED SHIFTS"
                  value={stats?.cancelled || 0}
                  Icon={NoSymbolIcon}
                  color="#C84648"
                  iconColor="#C84648"
                  borderColor="#C84648"
                />
                <StatCard
                  title="IN PROGRESS"
                  value={stats?.inProgress || 0}
                  Icon={ArrowPathIcon}
                  color="#3A7D44"
                  iconColor="#3A7D44"
                  borderColor="#3A7D44"
                />
              </div>

              {/* Today's Schedule */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    
                   
                  </div>
                  <div className="flex items-center space-x-2">
                    {/* Calendar icon removed */}
                  </div>
                </div>
                
                <TodaysSchedule openings={scheduleData} />
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}